import { isDesktopSafari } from '@lemonade-hq/cdk';
import type { GenericRecordData } from '@lemonade-hq/maschema-schema';
import type { UniclientComponentTitle, UniclientFrame } from '@lemonade-hq/uniclient-chat';
import { Flex, getCssVarName, Layout, spacing, useViewportSize } from '@lemonade-hq/uniclient-theme';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { forwardRef } from 'react';
import type { PropsWithChildren } from 'react';
import { WIZARD_CONTAINER_ELEMENT_ID } from '../utils/consts';
import { MOBILE_BOTTOM_SAFE_AREA } from './layout';
import type { WizardOnAction } from './types';
import { isHistoryVisibleVar, MAIN_GRID_COLUMNS } from './Wizard.css';
import * as styles from './WizardFrame.css';
import { HISTORY_MAIN_PADDING } from './WizardHistory.css';
import { WizardLayoutContainer } from './WizardLayoutContainer';
import { WizardProgress } from './WizardProgress';
import { WizardScrollSnapContainer } from './WizardScrollSnapContainer';
import { WizardSideGrid } from './WizardSideGrid';

import { WizardSummary } from './WizardSummary';

const isHistoryVisibleVarName = getCssVarName(isHistoryVisibleVar);

const paddingBottom = `calc(((${HISTORY_MAIN_PADDING} + ${spacing.s16}) * var(${isHistoryVisibleVarName}))  + ${MOBILE_BOTTOM_SAFE_AREA}px)`;

type WizardFrameProps = Omit<UniclientFrame, 'title'> &
  WizardOnAction & {
    readonly onOverlayClick?: () => void;
    readonly loading?: boolean;
    readonly title?: UniclientComponentTitle;
    readonly disabled?: boolean;
    readonly isTransitioningBetweenScripts: boolean;
    readonly answers?: GenericRecordData;
    readonly showFreeTextInput?: boolean;
    readonly hasActions?: boolean;
    readonly animationDelay?: number;
    readonly hasFooter?: boolean;
  };

export const WizardFrame = forwardRef<HTMLDivElement, PropsWithChildren<WizardFrameProps>>(
  (
    {
      children,
      onOverlayClick,
      onAction,
      progress,
      summary,
      type,
      loading,
      disabled = false,
      isTransitioningBetweenScripts,
      answers,
      showFreeTextInput = false,
      hasActions = false,
      animationDelay = 0,
      hasFooter = false,
    },
    ref,
  ) => {
    const { isBelowDesktopViewport } = useViewportSize();

    return (
      <WizardScrollSnapContainer
        className={styles.frame({ type })}
        justifyContent="center"
        pb={showFreeTextInput ? { sm: spacing.s32, md: 0 } : undefined}
        ref={ref}
        scrollSnapAlign="start"
      >
        <Flex
          as={motion.div}
          className={styles.frameContent({ hasSummary: summary != null || hasFooter, hasActions })}
          flexDirection={{
            sm: 'column',
            lg: 'row',
          }}
          gap={{ lg: spacing.s16 }}
          style={assignInlineVars({
            [styles.wizardFramePaddingBottom]: paddingBottom,
          })}
        >
          <WizardSideGrid
            className={clsx(styles.side, styles.sideLayer({ visible: progress != null }))}
            columns={styles.WIZARD_LEFT_SIDE_COLUMNS}
          >
            <AnimatePresence initial={false}>
              {progress != null && (
                <WizardLayoutContainer columns={styles.WIZARD_LEFT_SIDE_COLUMNS}>
                  <Layout
                    animate={{ opacity: 1, transition: { delay: 0.5 } }}
                    as={motion.div}
                    exit={{ opacity: 0 }}
                    gridColumn={`span ${styles.WIZARD_LEFT_SIDE_COLUMNS}`}
                    initial={{ opacity: 0 }}
                    layout="position"
                  >
                    <WizardProgress {...progress} onAction={onAction} />
                  </Layout>
                </WizardLayoutContainer>
              )}
            </AnimatePresence>
          </WizardSideGrid>
          <WizardLayoutContainer columns={MAIN_GRID_COLUMNS} noRowsBelowDesktop>
            <Flex
              flexDirection="column"
              gridColumn={{
                sm: `span ${MAIN_GRID_COLUMNS.sm}`,
                md: `span ${MAIN_GRID_COLUMNS.md}`,
                lg: `2 / span ${MAIN_GRID_COLUMNS.lg}`,
              }}
              id={WIZARD_CONTAINER_ELEMENT_ID}
              justifyContent="space-between"
            >
              {children}
            </Flex>
          </WizardLayoutContainer>
          <WizardSideGrid
            className={clsx(
              styles.side,
              styles.sideLayer({ visible: !isTransitioningBetweenScripts && summary != null }),
              styles.summaryContainer,
              { [styles.safariPositionOverride]: isDesktopSafari() },
            )}
            columns={{ sm: MAIN_GRID_COLUMNS.sm, md: styles.WIZARD_RIGHT_SIDE_COLUMNS }}
            showOnMobile={summary != null}
          >
            <Layout className={styles.summary}>
              <WizardSummary
                animationDelay={animationDelay}
                answers={answers}
                disabled={disabled}
                drawerExternalClassName={showFreeTextInput ? styles.fastTransition : undefined}
                hidden={isBelowDesktopViewport && showFreeTextInput}
                loading={loading}
                onAction={onAction}
                summary={isTransitioningBetweenScripts ? undefined : summary}
              />
            </Layout>
          </WizardSideGrid>
        </Flex>
        <div className={styles.frameScrollOverlay} onClick={() => onOverlayClick?.()} />
      </WizardScrollSnapContainer>
    );
  },
);
